import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Click sur le logo / "Accueil" : retour homepage avec scroll-top si déjà sur /
  const goHome = (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const closeMenu = () => setIsMenuOpen(false);

  // Liens vers de vraies routes (SEO propre — chaque page a son URL et ses méta)
  const navItems = [
    { name: "Services", to: "/services" },
    { name: "Portfolio", to: "/portfolio" },
    { name: "Avis", to: "/#avis", isAnchor: true }, // pas de page dédiée → ancre HomePage
    { name: "FAQ", to: "/faq" },
    { name: "Contact", to: "/contact" },
  ];

  // Pour l'ancre "Avis" : navigue vers homepage et scroll vers #avis
  const goToAnchor = (anchor) => (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    const id = anchor.replace("/#", "");
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-slate-900/95 backdrop-blur-sm border-b border-cyan-400/20"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo VyntraWeb */}
          <Link
            to="/"
            onClick={goHome}
            className="flex items-center space-x-4 group"
            data-testid="header-logo-link"
            aria-label="Retour à l'accueil VyntraWeb"
          >
            <div className="relative">
              <picture>
                <source
                  type="image/webp"
                  srcSet="/assets/images/logo-vyntraweb-64.webp 64w, /assets/images/logo-vyntraweb-128.webp 128w"
                  sizes="64px"
                />
                <img
                  src="/assets/images/logo-vyntraweb-128.png"
                  srcSet="/assets/images/logo-vyntraweb-64.png 64w, /assets/images/logo-vyntraweb-128.png 128w"
                  sizes="64px"
                  alt="VyntraWeb - Agence Web pour Commerces Locaux"
                  width="64"
                  height="64"
                  fetchPriority="high"
                  decoding="async"
                  className="h-16 w-auto transition-all duration-500 group-hover:scale-110 relative z-10"
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-r from-[#F87171]/20 to-cyan-400/20 rounded-lg blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>
            </div>
            <div className="hidden sm:block">
              <div className="text-xs text-slate-500 font-light tracking-wider uppercase opacity-70">
                Premium Web Agency
              </div>
            </div>
          </Link>

          {/* Navigation Desktop */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              onClick={goHome}
              className="text-slate-300 hover:text-cyan-400 transition-colors duration-300 relative group"
              data-testid="header-nav-accueil"
            >
              Accueil
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-cyan-400 transition-all duration-300 group-hover:w-full"></span>
            </Link>
            {navItems.map((item) =>
              item.isAnchor ? (
                <a
                  key={item.name}
                  href={item.to}
                  onClick={goToAnchor(item.to)}
                  className="text-slate-300 hover:text-cyan-400 transition-colors duration-300 relative group"
                  data-testid={`header-nav-${item.name.toLowerCase()}`}
                >
                  {item.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-cyan-400 transition-all duration-300 group-hover:w-full"></span>
                </a>
              ) : (
                <Link
                  key={item.name}
                  to={item.to}
                  onClick={closeMenu}
                  className="text-slate-300 hover:text-cyan-400 transition-colors duration-300 relative group"
                  data-testid={`header-nav-${item.name.toLowerCase()}`}
                >
                  {item.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-cyan-400 transition-all duration-300 group-hover:w-full"></span>
                </Link>
              )
            )}
          </nav>

          {/* CTA Button → page contact dédiée */}
          <div className="hidden md:block">
            <Link
              to="/contact"
              onClick={closeMenu}
              className="bg-[#F87171] hover:bg-[#F56565] text-white px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-[#F87171]/25 font-semibold inline-block"
              aria-label="Demander un devis"
              data-testid="header-cta-devis"
            >
              Demander un devis
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-slate-50"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={isMenuOpen}
            data-testid="header-mobile-toggle"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden mt-4 py-4 border-t border-cyan-400/20">
            <div className="flex flex-col space-y-4">
              <Link
                to="/"
                onClick={goHome}
                className="text-slate-300 hover:text-cyan-400 transition-colors duration-300 py-2"
                data-testid="header-nav-mobile-accueil"
              >
                Accueil
              </Link>
              {navItems.map((item) =>
                item.isAnchor ? (
                  <a
                    key={item.name}
                    href={item.to}
                    onClick={goToAnchor(item.to)}
                    className="text-slate-300 hover:text-cyan-400 transition-colors duration-300 py-2"
                    data-testid={`header-nav-mobile-${item.name.toLowerCase()}`}
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.to}
                    onClick={closeMenu}
                    className="text-slate-300 hover:text-cyan-400 transition-colors duration-300 py-2"
                    data-testid={`header-nav-mobile-${item.name.toLowerCase()}`}
                  >
                    {item.name}
                  </Link>
                )
              )}
              <Link
                to="/contact"
                onClick={closeMenu}
                className="bg-[#F87171] hover:bg-[#F56565] text-white mt-4 rounded-lg px-6 py-3 font-semibold inline-block text-center"
                aria-label="Demander un devis"
                data-testid="header-cta-devis-mobile"
              >
                Demander un devis
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
